import { useMemo } from 'react';

import Store from 'helpers/store';
import Cache from 'helpers/cache';

import useStore from 'hooks/useStore';
import ResourceService from 'services/resources';

const useResource = (resourceType, options = {}) => {
  const { prefetch = false } = options;

  const store = useMemo(() => {
    return new Store({
      list: [],
      isLoading: false,
    }, resourceType.name);
  }, [resourceType]);

  const actions = useMemo(() => {
    const actions = createResourceActions(store, resourceType);
    if (prefetch) {
      actions.getAll();
    }
    return actions;
  }, [store, resourceType, prefetch]);

  // subscribe to store
  useStore(store);
  
  return {
    state: store.get(),
    actions,
    store,
  };
};


const resourceCache = new Cache();

const createResourceActions = (store, resourceType) => {
  const { idField } = resourceType;
  const service = new ResourceService(resourceType);

  return {
    async getAll() {
      return Promise.resolve().then(() => {
        let list = resourceCache.get(resourceType.name);
        if (list) {
          store.set(() => ({ list }));
        }
        
        store.set(() => ({ isLoading: true }));
        return service.loadAll().then(data => {
          if (!data) return;
          resourceCache.set(resourceType.name, data, 60000);
          store.set(() => ({
            list: data,
          }));
          return data;
        })
        .catch(err => {
          store.set(() => ({ error: err.message }));
        })
        .finally(() => {
          if (store.get().isLoading) {
            store.set(() => ({ isLoading: false }));
          }
        });
      });
    },
  
    async get(id) {
      return Promise.resolve().then(() => {
        const state = store.get();
        const idx = state.list.findIndex(i => {
          return i[idField] === id;
        });
        if (idx !== -1) {
          return store.get().list[idx];
        } else {
          store.set(() => ({ isLoading: true }));
          return service.load(id).then(data => {
            if (!data) return;
            const item = data[0], id = item[idField];
            let found = false;
            store.set(state => ({
              list: state.list.map(i => {
                if (i[idField] === id) {
                  found = true;
                  return item;
                }
                return i;
              })
            }));
            if (!found) {
              store.set(state => ({
                list: [...state.list, item]
              }));
            }
          })
          .catch(err => {
            store.set(() => ({ error: err.message }));
          })
          .finally(() => {
            store.set(() => ({ isLoading: false }));
          });
        }
      });
    },
  
    async create(data) {
      store.set(() => ({ isLoading: true }));
      return service.create(data)
        .then(data => {
          if (!data) return;
          const item = data;
          store.set(state => ({
            list: [...state.list, item],
          }));
          return item;
        })
        .catch(err => {
          store.set(() => ({ error: err.message }));
        })
        .finally(() => {
          resourceCache.unset(resourceType.name);
          store.set(() => ({ isLoading: false }));
        });
    },
  
    async update(data) {
      store.set(() => ({ isLoading: true }));
      return service.update(data)
        .then(data => {
          if (!data) return;
          const item = data;
          store.set(state => ({
            list: state.list.map(i => {
              if (i[idField] === item[idField]) {
                return item;
              }
              return i;
            })
          }));
          return item;
        })
        .catch(err => {
          store.set(() => ({ error: err.message }));
        })
        .finally(() => {
          resourceCache.unset(resourceType.name);
          store.set(() => ({ isLoading: false }));
        });
    },
  
    async delete(id) {
      store.set(() => ({ isLoading: true }));
      return service.delete(id)
        .then(() => {
          store.set(state => ({
            list: state.list.filter(i => {
              return i[idField] !== id;
            })
          }));
        })
        .catch(err => {
          store.set(() => ({ error: err.message }));
        })
        .finally(() => {
          resourceCache.unset(resourceType.name);
          store.set(() => ({ isLoading: false }));
        });
    },
  }
};

export default useResource;
